(function () {
  const BLOCK_SELECTOR = '[data-addon-products-block]';
  const ITEM_SELECTOR = '[data-addon-item]';
  const blockControllers = new WeakMap();

  function parseQuantity(raw) {
    const parsed = Number.parseInt(String(raw || '1'), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  function getItemQuantity(item) {
    const input = item.querySelector('[data-addon-quantity]');
    if (input instanceof HTMLInputElement) {
      return parseQuantity(input.value);
    }
    return parseQuantity(item.getAttribute('data-addon-default-quantity'));
  }

  function hideMessage(block, selector) {
    const el = block.querySelector(selector);
    if (!el) return;
    el.hidden = true;
    const target = el.querySelector('[data-addon-warning-text]') || el;
    target.textContent = '';
  }

  function showMessage(block, selector, message) {
    const el = block.querySelector(selector);
    if (!el) return;
    el.hidden = false;
    const target = el.querySelector('[data-addon-warning-text]') || el;
    target.textContent = message;
  }

  function setEditorNote(block, message) {
    const note = block.querySelector('[data-addon-editor-note]');
    if (!note) return;
    note.hidden = !message;
    note.textContent = message || '';
  }

  function setFormBusy(form, busy) {
    form.setAttribute('aria-busy', busy ? 'true' : 'false');
    form.querySelectorAll('button[type="submit"], input[type="submit"]').forEach((btn) => {
      if (btn instanceof HTMLButtonElement || btn instanceof HTMLInputElement) {
        btn.disabled = busy;
      }
    });
  }

  function findSupportedForm(block) {
    const section = block.closest('.shopify-section') || block.closest('section') || block.closest('main') || document;
    const forms = section.querySelectorAll('form[action*="/cart/add"]');
    for (const form of forms) {
      if (form.querySelector('input[name="id"]') && form.querySelector('button[type="submit"], input[type="submit"]')) {
        return form;
      }
    }
    return null;
  }

  function getCartContext() {
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    return cart && typeof cart.renderContents === 'function' ? cart : null;
  }

  function toggleDynamicCheckout(form, hidden) {
    const wrapper = form.querySelector('.shopify-payment-button');
    if (!wrapper) return;

    wrapper.classList.toggle('pdp-addon-products__payment-button--hidden', hidden);

    if (!wrapper.hasAttribute('data-addon-original-aria-hidden')) {
      const existing = wrapper.getAttribute('aria-hidden');
      wrapper.setAttribute('data-addon-original-aria-hidden', existing == null ? '' : existing);
    }
    wrapper.setAttribute('aria-hidden', hidden ? 'true' : 'false');

    if ('inert' in wrapper) {
      if (!wrapper.hasAttribute('data-addon-original-inert')) {
        wrapper.setAttribute('data-addon-original-inert', wrapper.inert ? 'true' : 'false');
      }
      wrapper.inert = hidden;
    }

    wrapper.querySelectorAll('button, [role="button"], a, input, select, textarea').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      if (hidden) {
        if (!el.hasAttribute('data-addon-original-disabled')) {
          const isDisabled =
            el instanceof HTMLButtonElement || el instanceof HTMLInputElement ||
            el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement
              ? el.disabled : el.getAttribute('aria-disabled') === 'true';
          el.setAttribute('data-addon-original-disabled', isDisabled ? 'true' : 'false');
        }
        if (!el.hasAttribute('data-addon-tabindex')) {
          const existing = el.getAttribute('tabindex');
          el.setAttribute('data-addon-tabindex', existing == null ? '' : existing);
        }
        el.setAttribute('tabindex', '-1');
        el.setAttribute('aria-disabled', 'true');
        if (el instanceof HTMLButtonElement || el instanceof HTMLInputElement) el.disabled = true;
      } else {
        if (el.hasAttribute('data-addon-tabindex')) {
          const prev = el.getAttribute('data-addon-tabindex');
          if (prev === '') el.removeAttribute('tabindex');
          else if (prev != null) el.setAttribute('tabindex', prev);
          el.removeAttribute('data-addon-tabindex');
        }
        const wasDisabled = el.getAttribute('data-addon-original-disabled') === 'true';
        if (!wasDisabled) el.removeAttribute('aria-disabled');
        else el.setAttribute('aria-disabled', 'true');
        el.removeAttribute('data-addon-original-disabled');
        if (el instanceof HTMLButtonElement || el instanceof HTMLInputElement ||
            el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement) {
          el.disabled = wasDisabled;
        }
      }
    });

    if (!hidden) {
      const origAria = wrapper.getAttribute('data-addon-original-aria-hidden');
      if (origAria === '') wrapper.removeAttribute('aria-hidden');
      else if (origAria != null) wrapper.setAttribute('aria-hidden', origAria);
      wrapper.removeAttribute('data-addon-original-aria-hidden');
      if ('inert' in wrapper) {
        wrapper.inert = wrapper.getAttribute('data-addon-original-inert') === 'true';
        wrapper.removeAttribute('data-addon-original-inert');
      }
    }
  }

  async function postCartAdd(items, cart) {
    const body = { items };
    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname + window.location.search;
      if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
    }
    const response = await fetch(`${routes.cart_add_url}.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    });
    const parsed = await response.json().catch(() => null);
    return { response, parsed };
  }

  async function handleSuccessfulAdd(block, formData, parsed, cart) {
    if (!cart) {
      window.location.assign(window.routes?.cart_url || '/cart');
      return;
    }
    if (cart.tagName === 'CART-NOTIFICATION' && !parsed.key && Array.isArray(parsed.items) && parsed.items[0]?.key) {
      parsed.key = parsed.items[0].key;
    }
    if (!parsed?.status && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      await publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'pdp-addon-products',
        productVariantId: formData.get('id'),
        cartData: parsed,
      });
    }
    cart.renderContents(parsed);
    if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
  }

  // --- Variant helpers (scoped per item) ---

  function getVariants(item) {
    const script = item.querySelector('[data-addon-variants-json]');
    if (!(script instanceof HTMLScriptElement)) return [];
    try {
      const parsed = JSON.parse(script.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  }

  function getSelectedOptions(item) {
    const groups = new Map();
    item.querySelectorAll('[data-addon-option-button]').forEach((btn) => {
      if (!(btn instanceof HTMLButtonElement)) return;
      const idx = Number.parseInt(btn.dataset.optionIndex || '-1', 10);
      if (idx < 0) return;
      if (btn.classList.contains('is-selected')) groups.set(idx, btn.dataset.optionValue || '');
    });
    item.querySelectorAll('[data-addon-option-select]').forEach((sel) => {
      if (!(sel instanceof HTMLSelectElement)) return;
      const idx = Number.parseInt(sel.dataset.optionIndex || '-1', 10);
      if (idx < 0) return;
      groups.set(idx, sel.value);
    });
    return Array.from(groups.entries()).sort((a, b) => a[0] - b[0]).map((e) => e[1]);
  }

  function findVariantById(variants, id) {
    return variants.find((v) => String(v.id) === String(id)) || null;
  }

  function findMatchingVariant(variants, opts) {
    return variants.find((v) => {
      if (!v.available || !Array.isArray(v.options) || v.options.length !== opts.length) return false;
      return v.options.every((val, i) => val === opts[i]);
    }) || null;
  }

  function hasAvailableMatch(variants, selected, optIdx, candidate) {
    return variants.some((v) => {
      if (!v.available || !Array.isArray(v.options)) return false;
      return v.options.every((val, i) => {
        if (i === optIdx) return val === candidate;
        const sel = selected[i];
        return !sel || val === sel;
      });
    });
  }

  function syncOptionAvailability(item, variants) {
    const selected = getSelectedOptions(item);
    item.querySelectorAll('[data-addon-option-button]').forEach((btn) => {
      if (!(btn instanceof HTMLButtonElement)) return;
      const idx = Number.parseInt(btn.dataset.optionIndex || '-1', 10);
      const avail = hasAvailableMatch(variants, selected, idx, btn.dataset.optionValue || '');
      btn.disabled = !avail;
      if (!avail) { btn.classList.remove('is-selected'); btn.setAttribute('aria-pressed', 'false'); }
    });
    item.querySelectorAll('[data-addon-option-select]').forEach((sel) => {
      if (!(sel instanceof HTMLSelectElement)) return;
      const idx = Number.parseInt(sel.dataset.optionIndex || '-1', 10);
      Array.from(sel.options).forEach((opt) => { opt.disabled = !hasAvailableMatch(variants, selected, idx, opt.value); });
    });
  }

  function normalizeSelectedButtons(item) {
    const indexes = Array.from(new Set(
      Array.from(item.querySelectorAll('[data-addon-option-button]'))
        .map((b) => b instanceof HTMLButtonElement ? b.dataset.optionIndex || '' : '').filter(Boolean)
    )).sort();
    indexes.forEach((idx) => {
      const group = Array.from(
        item.querySelectorAll(`[data-addon-option-button][data-option-index="${CSS.escape(idx)}"]`)
      ).filter((b) => b instanceof HTMLButtonElement);
      if (group.find((b) => b.classList.contains('is-selected') && !b.disabled)) return;
      const first = group.find((b) => !b.disabled);
      if (first instanceof HTMLButtonElement) setSelectedButton(first);
    });
  }

  function setSelectedButton(button) {
    if (!(button instanceof HTMLButtonElement)) return;
    const idx = button.dataset.optionIndex;
    if (idx == null) return;
    const item = button.closest(ITEM_SELECTOR);
    if (!item) return;
    item.querySelectorAll(`[data-addon-option-button][data-option-index="${CSS.escape(idx)}"]`).forEach((b) => {
      if (!(b instanceof HTMLButtonElement)) return;
      const sel = b === button;
      b.classList.toggle('is-selected', sel);
      b.setAttribute('aria-pressed', sel ? 'true' : 'false');
    });
  }

  function syncItemState(item, variants) {
    const isMulti = item.getAttribute('data-addon-multi-variant') === 'true';
    if (isMulti) {
      syncOptionAvailability(item, variants);
      normalizeSelectedButtons(item);
    }

    const selected = getSelectedOptions(item);
    const variant = selected.length > 0
      ? findMatchingVariant(variants, selected)
      : findVariantById(variants, item.getAttribute('data-addon-variant-id'));

    const checkbox = item.querySelector('[data-addon-checkbox]');
    const qtyInput = item.querySelector('[data-addon-quantity]');
    const priceNode = item.querySelector('[data-addon-price]');
    const statusNode = item.querySelector('[data-addon-status]');

    item.setAttribute('data-addon-variant-id', variant ? String(variant.id) : '');

    if (priceNode instanceof HTMLElement && variant?.price) {
      priceNode.textContent = variant.price + ' /ea';
    }

    const avail = Boolean(variant?.available);
    if (checkbox instanceof HTMLInputElement) {
      checkbox.disabled = !avail;
      if (!avail) checkbox.checked = false;
    }
    if (qtyInput instanceof HTMLInputElement) qtyInput.disabled = !avail;
    if (statusNode instanceof HTMLElement) statusNode.hidden = avail;
  }

  // --- Submission ---

  function anyAddonChecked(block) {
    return Array.from(block.querySelectorAll('[data-addon-checkbox]')).some(
      (cb) => cb instanceof HTMLInputElement && cb.checked
    );
  }

  async function handleSubmit(event, block, form) {
    if (!anyAddonChecked(block)) {
      hideMessage(block, '[data-addon-warning]');
      return;
    }

    const formData = new FormData(form);
    const mainVariantId = formData.get('id');
    if (!mainVariantId) {
      showMessage(block, '[data-addon-warning]',
        block.getAttribute('data-variant-validation-note') || 'Select a valid product variant.');
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    const items = block.querySelectorAll(ITEM_SELECTOR);
    const addonItems = [];

    for (const item of items) {
      const cb = item.querySelector('[data-addon-checkbox]');
      if (!(cb instanceof HTMLInputElement) || !cb.checked) continue;

      const variantId = item.getAttribute('data-addon-variant-id');
      if (!variantId) {
        showMessage(block, '[data-addon-warning]',
          block.getAttribute('data-addon-variant-validation-note') || 'Select a valid add-on option.');
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      addonItems.push({
        id: variantId,
        quantity: getItemQuantity(item),
        properties: { _addon_source: 'pdp-block-products' },
      });
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const cart = getCartContext();
    const mainQty = parseQuantity(formData.get('quantity'));
    const genericError = block.getAttribute('data-generic-error-message') || 'Could not add items. Please try again.';
    const partialWarning = block.getAttribute('data-warning-message') ||
      'The main item was added, but one or more add-ons became unavailable.';

    hideMessage(block, '[data-addon-warning]');
    setFormBusy(form, true);

    const scaledAddons = addonItems.map((a) => ({ ...a, quantity: mainQty * a.quantity }));
    const combinedItems = [{ id: String(mainVariantId), quantity: mainQty }, ...scaledAddons];

    try {
      const combined = await postCartAdd(combinedItems, cart);
      if (combined.response.ok && combined.parsed) {
        await handleSuccessfulAdd(block, formData, combined.parsed, cart);
        return;
      }

      const mainOnly = await postCartAdd([{ id: String(mainVariantId), quantity: mainQty }], cart);
      if (mainOnly.response.ok && mainOnly.parsed) {
        showMessage(block, '[data-addon-warning]', partialWarning);
        await handleSuccessfulAdd(block, formData, mainOnly.parsed, cart);
        return;
      }

      showMessage(block, '[data-addon-warning]', genericError);
    } catch (err) {
      console.error(err);
      showMessage(block, '[data-addon-warning]', genericError);
    } finally {
      setFormBusy(form, false);
    }
  }

  // --- Setup ---

  function setupBlock(block) {
    const existing = blockControllers.get(block);
    if (existing) existing.abort();

    const form = findSupportedForm(block);
    const items = block.querySelectorAll(ITEM_SELECTOR);

    if (!form || items.length === 0) {
      if (window.Shopify?.designMode) {
        setEditorNote(block,
          block.getAttribute('data-unsupported-theme-message') || 'Product form not supported.');
        block.hidden = false;
        return;
      }
      block.hidden = true;
      return;
    }

    const controller = new AbortController();
    blockControllers.set(block, controller);
    setEditorNote(block, '');

    items.forEach((item) => {
      const variants = getVariants(item);
      const isMulti = item.getAttribute('data-addon-multi-variant') === 'true';

      if (isMulti && variants.length > 0) {
        syncItemState(item, variants);

        item.querySelectorAll('[data-addon-option-button]').forEach((btn) => {
          if (!(btn instanceof HTMLButtonElement)) return;
          btn.addEventListener('click', () => {
            if (btn.disabled) return;
            hideMessage(block, '[data-addon-warning]');
            setSelectedButton(btn);
            syncItemState(item, variants);
          }, { signal: controller.signal });
        });

        item.querySelectorAll('[data-addon-option-select]').forEach((sel) => {
          if (!(sel instanceof HTMLSelectElement)) return;
          sel.addEventListener('change', () => {
            hideMessage(block, '[data-addon-warning]');
            syncItemState(item, variants);
          }, { signal: controller.signal });
        });
      }

      const checkbox = item.querySelector('[data-addon-checkbox]');
      if (checkbox instanceof HTMLInputElement) {
        checkbox.addEventListener('change', () => {
          hideMessage(block, '[data-addon-warning]');
          toggleDynamicCheckout(form, anyAddonChecked(block));
        }, { signal: controller.signal });
      }
    });

    const submitTarget = form.closest('product-form') || form.parentElement || form;
    submitTarget.addEventListener('submit', (e) => {
      void handleSubmit(e, block, form);
    }, { capture: true, signal: controller.signal });
  }

  function init() {
    document.querySelectorAll(BLOCK_SELECTOR).forEach((block) => {
      if (!(block instanceof HTMLElement) || block.dataset.addonProductsReady === 'true') return;
      block.dataset.addonProductsReady = 'true';
      setupBlock(block);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:reorder', init);
  document.addEventListener('shopify:section:unload', (event) => {
    if (!(event.target instanceof Element)) return;
    event.target.querySelectorAll(BLOCK_SELECTOR).forEach((block) => {
      if (!(block instanceof HTMLElement)) return;
      const ctrl = blockControllers.get(block);
      if (ctrl) { ctrl.abort(); blockControllers.delete(block); }
      delete block.dataset.addonProductsReady;
    });
  });
})();
